
import { Link } from "react-router-dom";
import { useAppStore } from "@/lib/store";
import RevealOnScroll from "@/components/ui/RevealOnScroll";
import AnimatedHeadline from "@/components/ui/AnimatedHeadline";
import PageHero from "@/components/ui/PageHero";
import { useDocumentMeta } from "@/lib/useDocumentMeta";

const steps = [
  {
    n: "01",
    title: "List",
    copy: "Snap a few photos and describe your item in minutes.",
    image: "/images/steps/step-list.webp",
  },
  {
    n: "02",
    title: "Get Approved",
    copy: "Our team reviews every listing before it goes live.",
    image: "/images/steps/step-approved.webp",
  },
  {
    n: "03",
    title: "Sell",
    copy: "Your item joins the Multiverse for collectors to discover.",
    image: "/images/steps/step-sell.webp",
  },
  {
    n: "04",
    title: "Ship",
    copy: "Pack it up and send it to its next home.",
    image: "/images/steps/step-ship.webp",
  },
];

export default function SellPage() {
  useDocumentMeta({
    title: "Sell Your Stuff",
    description:
      "List your second-hand collectibles, figures, comics and gaming gear for sale on Nerd Multiverse. Every listing is reviewed before it goes live.",
    path: "/sell",
  });

  const authReady = useAppStore((s) => s._authReady);
  const user = useAppStore((s) => s.currentUser());
  const applyToSell = useAppStore((s) => s.applyToSell);

  return (
    <div className="pb-24 min-h-dvh">
      <PageHero
        desktop="/images/hero/sell-hero.webp"
        mobile="/images/hero/sell-mobile-v2.webp"
        alt="A masked seller photographing, listing, packing and shipping a collectible across the multiverse"
        heightClass="min-h-[560px] md:min-h-[480px]"
      >
        <RevealOnScroll>
          <span className="nm-kicker">Sell Your Stuff</span>
          <AnimatedHeadline
            as="h1"
            text="GOT SOMETHING WORTH COLLECTING?"
            className="mt-5 font-display text-4xl md:text-5xl font-black tracking-[-.045em] text-white leading-tight"
          />
          <p className="mt-5 max-w-2xl text-[var(--nm-ink-muted)]">
            Turn the stuff sitting on your shelf into someone else&apos;s next
            favourite find.
          </p>

          {!authReady ? null : !user ? (
            <Link
              to="/signup"
              className="nm-btn-primary mt-8"
            >
              CREATE A SELLER ACCOUNT
            </Link>
          ) : user.role === "seller" && user.sellerStatus === "approved" ? (
            <Link
              to="/dashboard/seller/add-product"
              className="mt-8 inline-block px-7 py-3.5 rounded-full bg-white text-[#05070d] text-sm font-medium tracking-wide hover:bg-[var(--nm-blue-bright)] hover:text-white transition-colors"
            >
              LIST AN ITEM
            </Link>
          ) : user.sellerStatus === "pending" ? (
            <p className="mt-8 text-sm text-[var(--nm-blue-bright)]">
              Your seller application is under review.
            </p>
          ) : (
            <button
              onClick={() => applyToSell(user.uid)}
              className="mt-8 px-7 py-3.5 rounded-full bg-white text-[#05070d] text-sm font-medium tracking-wide hover:bg-[var(--nm-blue-bright)] hover:text-white transition-colors"
            >
              APPLY TO SELL
            </button>
          )}
        </RevealOnScroll>
      </PageHero>

      <div className="nm-container max-w-2xl">
        <div className="mt-20 grid grid-cols-2 gap-x-8 gap-y-12">
          {steps.map((s, i) => (
            <RevealOnScroll key={s.n} delay={i * 0.08}>
              <div className="w-14 h-14 rounded-2xl overflow-hidden border border-white/10 bg-white/5">
                <img src={s.image} alt="" className="w-full h-full object-cover" />
              </div>
              <span className="mt-3 block font-display text-sm text-[var(--nm-blue-bright)]">
                {s.n}
              </span>
              <p className="mt-1 font-display text-xl font-semibold text-white">
                {s.title}
              </p>
              <p className="mt-2 text-sm text-[var(--nm-ink-muted)]">{s.copy}</p>
            </RevealOnScroll>
          ))}
        </div>
      </div>
    </div>
  );
}
